import { readDevCyclePregnancyStatus } from "@src/packages/cycles/dev/devActiveCycleOverride";
import type { PregnancyStatusReading } from "./resolvePregnancyStatus";
/**
 * Where the patient says her pregnancy stands, as the server keeps it.
 *
 * This is a *separate fact* from the cycle's clinical outcome. The app used to
 * record it by correcting that outcome — which fails outright when the positive
 * result was authored by a clinic or a provider, because a patient may only
 * correct her own, and which quietly rewrote a clinical record to carry a
 * patient's statement. `/cycles/{cycleId}/pregnancy-status` is the contract that
 * replaces it (louise-life/louise-patient-expo#3492): the positive outcome stays
 * exactly as it was recorded and keeps its provenance, and her answer is stored
 * as her own, append-only, linked to that outcome.
 *
 * Four things stay distinct, and this module is only the third of them: the beta
 * measurement, the clinical outcome, the patient's pregnancy status, and the
 * pregnancy-follow-up configuration in `useCyclePregnancyFollowUp`.
 *
 * `current` is null whenever there is nothing she has said that still speaks for
 * the cycle's positive result — never recorded, or recorded against an outcome
 * that has since been corrected away. That is not an error to paper over: the
 * app must say nothing rather than infer a status from a lab value.
 */

import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { useApiClient, type ApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import {
  cycleKeys,
  cycleMutationInvalidationKeys,
} from "@src/packages/cycles/cycle-cache-keys";
import type { paths } from "@src/packages/openapi/schema";
import { pregnancyFollowUpKeys } from "./useCyclePregnancyFollowUp";
import {
  CyclePregnancyStatusWriteError,
  recordCyclePregnancyStatus,
} from "./recordCyclePregnancyStatus";
export { CyclePregnancyStatusWriteError } from "./recordCyclePregnancyStatus";

type PregnancyStatusPath = paths["/cycles/{cycleId}/pregnancy-status"];

export type CyclePregnancyStatusAggregate =
  PregnancyStatusPath["get"]["responses"][200]["content"]["application/json"]["data"];

/** One stored version of the assertion — the current one, or a corrected one. */
export type CyclePregnancyStatusRecord = NonNullable<
  CyclePregnancyStatusAggregate["current"]
>;

export type CyclePregnancyStatusWrite = NonNullable<
  PregnancyStatusPath["post"]["requestBody"]
>["content"]["application/json"];

/**
 * The three states, taken from the generated client rather than restated here.
 * A backend that adds a fourth breaks the build at every reader instead of
 * silently falling through it.
 */
export type PatientPregnancyStands = CyclePregnancyStatusRecord["status"];
export type PatientPregnancyLossType = NonNullable<
  CyclePregnancyStatusRecord["lossType"]
>;
export type PatientPregnancyBirthType = NonNullable<
  CyclePregnancyStatusRecord["birthType"]
>;
export type PatientPregnancyDatePrecision = NonNullable<
  CyclePregnancyStatusRecord["eventDatePrecision"]
>;

/**
 * Derived from the canonical cycle registry rather than declared here (#2240):
 * a cycle-outcome write has to invalidate this query, and a second spelling of
 * the prefix is exactly how the cycle caches drifted apart before.
 */
export const pregnancyStatusKeys = {
  all: cycleKeys.pregnancyStatusAll,
  cycle: cycleKeys.pregnancyStatus,
};

/**
 * Reads what she has said about this cycle's pregnancy, and everything she said
 * before it.
 *
 * Disabled without a cycle rather than fetching a guess, as the follow-up read
 * is: the assertion is scoped to one cycle's positive outcome.
 */
function pregnancyStatusQueryOptions(
  client: ApiClient,
  cycleId?: string | null,
) {
  return {
    queryKey: pregnancyStatusKeys.cycle(cycleId ?? ""),
    queryFn: async (): Promise<CyclePregnancyStatusAggregate | null> => {
      if (!cycleId) return null;
      const fixture = readDevCyclePregnancyStatus(cycleId);
      if (fixture) return fixture;
      const failed = (cause: unknown, status?: number): never =>
        throwApiError(
          cause,
          "Failed to load the pregnancy status",
          "cycle-pregnancy-status",
          status,
        );
      try {
        const { data, error, response } = await client.GET(
          "/cycles/{cycleId}/pregnancy-status",
          { params: { path: { cycleId } } },
        );
        /**
         * A refused or broken read is a failure, not "she has said nothing".
         *
         * openapi-fetch resolves rather than throws on a non-2xx, so returning
         * `data?.data ?? null` turned a 500 into a silent null — and null here
         * means no statement, which every reader takes as a pregnancy still
         * standing. A patient who had recorded a loss would have been shown her
         * first-scan guidance for as long as the endpoint was down. Raised so the
         * query goes to `isError` and the gates withhold instead.
         */
        if (error || !response.ok) failed(error, response.status);
        return data?.data ?? null;
      } catch (error) {
        return failed(error);
      }
    },
    enabled: Boolean(cycleId),
    staleTime: 1000 * 60 * 5,
  };
}

/** One cached read per candidate cycle, shared by Home's selector, hero and guidance. */
export function useCyclePregnancyStatuses(cycleIds: readonly string[]) {
  const { client } = useApiClient();
  return useQueries({
    queries: cycleIds.map((cycleId) =>
      pregnancyStatusQueryOptions(client, cycleId),
    ),
    combine: (results) =>
      Object.fromEntries(
        results.map((query, index) => [
          cycleIds[index],
          query.isPending || query.isError
            ? "unknown"
            : (query.data?.current ?? null),
        ]),
      ) as Record<string, PregnancyStatusReading>,
  });
}

export function useCyclePregnancyStatus(cycleId?: string | null) {
  const { client } = useApiClient();
  const query = useQuery(pregnancyStatusQueryOptions(client, cycleId));
  const current: PregnancyStatusReading = !cycleId
    ? null
    : query.isPending || query.isError
      ? "unknown"
      : (query.data?.current ?? null);
  // Resolution decides what may be displayed. Lineage decides what a new
  // statement must supersede, even after the linked outcome became unresolved.
  const head: PregnancyStatusReading =
    current === "unknown"
      ? "unknown"
      : (query.data?.history.find(
          (version) => version.recordStatus === "current",
        ) ?? null);
  return { ...query, current, head };
}

/**
 * Records where the pregnancy stands, as a new patient-owned fact.
 *
 * Nothing here touches the outcome. A second answer is an append-only
 * correction of the first — `supersedesStatusId` plus the reason — which the API
 * requires whenever a current version exists, so the caller must pass the record
 * it read.
 *
 * Invalidates the follow-up alongside its own key and the cycle family: an ended
 * or delivered pregnancy closes an opted-in follow-up *in the same server
 * command*, so a cached follow-up would otherwise keep deriving a gestational age
 * for a pregnancy she has just told us is over.
 */
export function useRecordCyclePregnancyStatus() {
  const { client } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      body,
      afterRecord,
    }: {
      cycleId: string;
      body: CyclePregnancyStatusWrite;
      afterRecord?: () => Promise<void>;
    }): Promise<CyclePregnancyStatusAggregate> => {
      const result = await recordCyclePregnancyStatus(client, {
        cycleId,
        body,
      });
      await afterRecord?.();
      return result;
    },
    onSuccess: (_result, { cycleId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: pregnancyStatusKeys.cycle(cycleId),
        }),
        queryClient.invalidateQueries({
          queryKey: pregnancyFollowUpKeys.cycle(cycleId),
        }),
        ...cycleMutationInvalidationKeys().map((key) =>
          queryClient.invalidateQueries({ queryKey: key }),
        ),
      ]),
    /**
     * A conflict re-reads. Nothing else does.
     *
     * The API refuses a write that does not supersede the record it actually
     * holds, and the copy the screen shows for that says "we've refreshed it,
     * so save again". Invalidating only on success made that a lie: the caller kept
     * the stale record it had already been refused for, so saving again repeated
     * the same conflict forever. The cycle family goes with it, because a
     * conflict can equally mean the *outcome* this was aimed at is no longer the
     * cycle's current positive one.
     *
     * Narrowed to 409 on purpose. Refreshing after a timeout would be actively
     * harmful: a POST that committed and lost its response would come back as a
     * new current record, the retry would re-target it, mint a second request id
     * and append a duplicate version of what she just said — the exact double
     * write the retained id exists to prevent.
     */
    onError: (error, { cycleId }) => {
      if (
        !(error instanceof CyclePregnancyStatusWriteError) ||
        !error.isConflict
      ) {
        return;
      }
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: pregnancyStatusKeys.cycle(cycleId),
        }),
        ...cycleMutationInvalidationKeys().map((key) =>
          queryClient.invalidateQueries({ queryKey: key }),
        ),
      ]);
    },
  });
}
